import React, { useMemo, useState, createContext, useContext } from 'react';
import { ModuleId, Question } from '../../../types';
export type QualitySeverityFilter = 'all' | 'critical' | 'warning' | 'info';
export type QualityTab = 'governance' | 'quality' | 'didactic-depth' | 'content' | 'seeds' | 'visuals';
export interface QualityIssue {
  id: string;
  category: QualityTab;
  severity: 'critical' | 'warning' | 'info';
  message: string;
}
export interface QualityQuestionRecord {
  question: Question;
  issues: QualityIssue[];
  /** Quality score 0–100 from questionQualityEngine */
  qualityScore?: number;
  /** Quick flags from quality engine */
  qualityFlags?: Array<{
    type: 'critical' | 'warning' | 'info';
    label: string;
  }>;
}
interface WorkspaceContextValue {
  records: QualityQuestionRecord[];
  filteredRecords: QualityQuestionRecord[];
  selectedModule: ModuleId | 'all';
  selectedTier: number | 'all';
  selectedGroup: number | 'all';
  search: string;
  severity: QualitySeverityFilter;
  activeTab: QualityTab;
  setSelectedModule: (value: ModuleId | 'all') => void;
  setSelectedTier: (value: number | 'all') => void;
  setSelectedGroup: (value: number | 'all') => void;
  setSearch: (value: string) => void;
  setSeverity: (value: QualitySeverityFilter) => void;
  setActiveTab: (value: QualityTab) => void;
}
const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);
interface ProviderProps {
  records: QualityQuestionRecord[];
  children: React.ReactNode;
}
export function QualityWorkspaceProvider({
  records,
  children
}: ProviderProps) {
  const [selectedModule, setSelectedModule] = useState<ModuleId | 'all'>('all');
  const [selectedTier, setSelectedTier] = useState<number | 'all'>('all');
  const [selectedGroup, setSelectedGroup] = useState<number | 'all'>('all');
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState<QualitySeverityFilter>('all');
  const [activeTab, setActiveTab] = useState<QualityTab>('governance');
  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    return records.filter(record => {
      const {
        question
      } = record;
      if (selectedModule !== 'all' && question.module !== selectedModule) return false;
      if (selectedTier !== 'all' && question.tier !== selectedTier) return false;
      if (selectedGroup !== 'all' && question.group !== selectedGroup) return false;
      if (query && !`${question.id} ${question.familyId} ${question.question}`.toLowerCase().includes(query)) return false;
      const tabIssues = record.issues.filter(issue => issue.category === activeTab);
      if (severity !== 'all' && !tabIssues.some(issue => issue.severity === severity)) return false;
      return true;
    });
  }, [records, selectedModule, selectedTier, selectedGroup, search, activeTab, severity]);
  return <WorkspaceContext.Provider value={{
    records,
    filteredRecords,
    selectedModule,
    selectedTier,
    selectedGroup,
    search,
    severity,
    activeTab,
    setSelectedModule,
    setSelectedTier,
    setSelectedGroup,
    setSearch,
    setSeverity,
    setActiveTab
  }} data-id="element-2696">
      {children}
    </WorkspaceContext.Provider>;
}
export function useQualityWorkspaceState() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useQualityWorkspaceState must be used inside QualityWorkspaceProvider');
  }
  return context;
}